import {
  type CSSProperties,
  type TransitionEvent,
  useCallback,
  useMemo,
} from "react";
import { useMediaQuery } from "../../hooks";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { useDrawerContext } from "./context";
import { DrawerHeader } from "./DrawerHeader";
import type { DrawerSide } from "./types";

export const DrawerContent = () => {
  const {
    title,
    description,
    children,
    side,
    className,
    floatingContext,
    getFloatingProps,
    handleAnimationEnd,
    visible,
    enterDuration = 200,
    exitDuration = 150,
    titleId,
    descriptionId,
  } = useDrawerContext();

  const { rtl } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 639px)");

  const effectiveSide = isMobile ? "bottom" : side || "right";

  const positionStyles = {
    right: "inset-y-0 end-0",
    left: "inset-y-0 start-0",
    top: "inset-x-0 top-0",
    bottom: "inset-x-0 bottom-0",
  };

  const defaultSizes = {
    right: "w-96",
    left: "w-96",
    top: "h-96 w-full",
    bottom: isMobile ? "max-h-[95vh] w-full" : "h-96 w-full",
  };

  const onTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>): void => {
      if (event.target === event.currentTarget) {
        handleAnimationEnd();
      }
    },
    [handleAnimationEnd],
  );

  const getTransformValue = (side: DrawerSide, isVisible: boolean): string => {
    if (isVisible) return "translate(0, 0)";
    const transforms: Record<DrawerSide, string> = {
      right: rtl ? "translateX(-100%)" : "translateX(100%)",
      left: rtl ? "translateX(100%)" : "translateX(-100%)",
      top: "translateY(-100%)",
      bottom: "translateY(100%)",
    };
    return transforms[side];
  };

  const transitionStyle = useMemo((): CSSProperties => {
    return {
      transitionProperty: "transform",
      transitionDuration: `${visible ? enterDuration : exitDuration}ms`,
      transitionTimingFunction: visible ? "ease-out" : "ease-in",
      transform: getTransformValue(effectiveSide, visible),
    };
  }, [visible, enterDuration, exitDuration, effectiveSide, rtl]);

  const getBorderStyles = () => {
    if (effectiveSide === "bottom") {
      return "border-t border-neutral-500/10";
    }
    if (effectiveSide === "top") {
      return "border-b border-neutral-500/10";
    }
    if (effectiveSide === "left") {
      return "border-e border-neutral-500/10";
    }
    return "border-s border-neutral-500/10";
  };

  const getRoundedCorners = () => {
    if (!isMobile) {
      return "";
    }

    if (effectiveSide === "bottom") {
      return "rounded-t-xl";
    }
    if (effectiveSide === "top") {
      return "rounded-b-xl";
    }
    return "";
  };

  const baseStyles = cn(
    "fixed z-[70] bg-white shadow-xl flex flex-col",
    getBorderStyles(),
    getRoundedCorners(),
  );

  return (
    <div
      ref={floatingContext.refs.setFloating}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
      data-state={visible ? "open" : "closed"}
      data-side={effectiveSide}
      className={cn(
        baseStyles,
        positionStyles[effectiveSide],
        defaultSizes[effectiveSide],
        className,
      )}
      style={transitionStyle}
      tabIndex={-1}
      onTransitionEnd={onTransitionEnd}
      {...getFloatingProps()}
    >
      {(title || description) && <DrawerHeader />}
      {children}
    </div>
  );
};
